import type { TestAnswerFeedbackSchemaType } from '@vedalib/editor/lib/brand'
import type { CSSProperties } from 'styled-components'

import type { ElementFeedbackCss } from 'services/Branding/types'
import { PreviewMode } from 'services/Store/Project/enums'
import { useProjectContext } from 'services/Store/Project/hooks'
import { getIsInteractiveMode } from 'services/Store/Project/selectors'
import type { IBlockMode, IBlockResult } from 'services/Store/Project/types'

import { AnswerStatusStyled, StyledFail, StyledSuccess } from '../styled/AnswerStatusStyled'
import AnswerWrapperStyled from '../styled/AnswerStyled'
import * as s from './AnswerFeedback.module.scss'

type IAnswerFeedbackProps = {
  children: React.ReactNode
  isVisible?: boolean | null
  isTrueAnswer?: boolean
  styles: ElementFeedbackCss<TestAnswerFeedbackSchemaType>
  blockResult?: IBlockResult
  mode?: IBlockMode
}

const AnswerFeedback = ({
  isVisible = false,
  isTrueAnswer = false,
  children,
  styles,
  blockResult,
  mode,
}: IAnswerFeedbackProps) => {
  const isPreview = useProjectContext(getIsInteractiveMode)
  const isDemoMode = mode?.previewMode === PreviewMode.demo
  const showIcon = isPreview && !isDemoMode ? !!blockResult : isVisible
  const label = isTrueAnswer ? 'Correct' : 'Incorrect'

  let iconComponent = null
  if (isTrueAnswer) {
    if (styles.correct?.backgroundImage) {
      iconComponent = (
        <div className={s.image} style={styles.correct as CSSProperties} aria-hidden />
      )
    } else {
      iconComponent = <StyledSuccess aria-hidden />
    }
  } else if (styles.incorrect?.backgroundImage) {
    iconComponent = (
      <div className={s.image} style={styles.incorrect as CSSProperties} aria-hidden />
    )
  } else {
    iconComponent = <StyledFail aria-hidden />
  }

  return (
    <div className={s.root}>
      <AnswerWrapperStyled $isVisible={isVisible || !isPreview} $styles={styles}>
        <AnswerStatusStyled
          $isTrueAnswer={isTrueAnswer}
          $isVisible={showIcon}
          $isVisibleIcon={isVisible}
          $styles={styles}
          aria-hidden={!showIcon}
        >
          {isVisible && iconComponent}
        </AnswerStatusStyled>
        <div aria-label={showIcon ? label : undefined} className={s.content}>
          {children}
        </div>
      </AnswerWrapperStyled>
    </div>
  )
}

export default AnswerFeedback
